/**
 * useTileViewModel — fetches data for a single tile through the CacheClient
 * and maps it to a GlanceViewModel via viewModelFromResponse.
 *
 * All tiles (loading / ok / stale / error / rateLimited) go through this hook.
 * The view-model factories (loadingView / errorView / rateLimitedView) are the
 * single shared path for non-data states — no hand-rolled chrome anywhere.
 *
 * Immutability: the view-model object is the cache envelope re-mapped — never
 * mutated after production. setState always receives a new object.
 *
 * Fix (D5a): clock stale-closure — tz/format param changes now take effect
 * without remount by including tile.source (serialised) in effect deps.
 *
 * Enrichment (D6): the hook now also returns the raw normalized data payload
 * from the cache envelope alongside the vm, so TileCell can write both into
 * the vmRegistry for the detail sheet to consume (e.g. WeatherDetail needs
 * humidity/wind/tempC which are not in the GlanceViewModel slots).
 *
 * clientDirect routing (ADR-004): tiles marked clientDirect in the registry
 * are routed to local data providers instead of the CacheClient. Three families:
 *   - clock.local        : noFetch, 1-second local interval (no network)
 *   - note.sticky        : noFetch, local — data from params, no timer needed
 *   - todo.list          : noFetch, local — data from params, no timer needed
 *   - countdown.event    : noFetch, local — data from params + Date; 60s interval
 *   - calendar.peek      : clientDirect but not noFetch; refresh-interval-driven
 *                          fetch through google-client directly to Google API.
 * One mechanism (TILE_REGISTRY[type].clientDirect) replaces the old hardcoded
 * `tile.type === 'clock.local'` check, so clock and calendar share one path.
 * noFetch stays for its existing consumers (semantics unchanged).
 *
 * Connect-state machine for calendar:
 *   disconnected → vm shows primary 'Connect', needsConnect: true
 *   expired      → silent re-acquire attempted; if fails → needsConnect: true
 *   not_configured → primary 'No key', needsConnect: false (env issue, not user)
 *   error        → standard error chrome
 */
import { useEffect, useMemo, useState } from 'react';
import {
  loadingView,
  viewModelFromResponse,
  isCacheData,
  clampGlanceViewModel,
  type GlanceViewModel,
  type CacheClient,
  type CacheRequest,
} from 'briefpane/contracts';
import type { TileRegistry } from 'briefpane/contracts';
import type { GlanceMap } from 'briefpane/schema';
import type { Tile } from 'briefpane/schema';
import { getTileTypeDef } from 'briefpane/schema';
// Local widget adapters — static imports (noFetch, no server bundle risk).
import { formatClockData } from '../../adapters/clock/format.js';
import { computeNoteData, noteAdapter as localNoteAdapter } from '../../adapters/local/note.js';
import { computeTodoData, todoAdapter as localTodoAdapter } from '../../adapters/local/todo.js';
import { computeCountdownData, countdownAdapter as localCountdownAdapter } from '../../adapters/local/countdown.js';

interface UseTileViewModelOptions {
  readonly tile: Tile;
  readonly cache: CacheClient;
  readonly registry: TileRegistry;
}

/** The combined result: the glance view-model plus the raw normalized data (if available). */
export interface TileViewModelResult {
  readonly vm: GlanceViewModel;
  /**
   * Raw normalized data from the cache envelope or local provider.
   * Null for noFetch tiles (clock.local) and error/loading states.
   * For calendar tiles, also carries events via the vmRegistry (see TileCell).
   */
  readonly data: Record<string, unknown> | null;
  /**
   * True when the tile is a clientDirect tile in a disconnected state that
   * requires the user to explicitly connect. TileCell renders a calm "Connect"
   * button overlay when this is true. False for all other states.
   */
  readonly needsConnect: boolean;
}

/**
 * Fetch the tile's data and return a GlanceViewModel + raw enrichment data.
 * Re-fetches when the tile config changes (new tile identity = new effect run).
 *
 * Routing (ADR-004):
 *   noFetch tiles (clock.local) → local 1-second interval; data is null
 *   clientDirect + !noFetch tiles (calendar.peek) → google-client fetch
 *     with refresh-interval-driven polling; needsConnect when disconnected
 *   shared-data tiles → CacheClient.fetch() (unchanged)
 */
export function useTileViewModel({ tile, cache, registry }: UseTileViewModelOptions): TileViewModelResult {
  const label = tile.label ?? null;
  const [vm, setVm] = useState<GlanceViewModel>(() => loadingView(label));
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [needsConnect, setNeedsConnect] = useState(false);

  // Stable serialised key for tile.source — changing tz/format triggers the clock
  // interval to restart (fixes stale-closure: old interval captured old tz/format).
  const sourceKey = useMemo(
    () => tile.source ? JSON.stringify(tile.source.params) : '',
    [tile.source],
  );

  // Stable serialised key for tile.glance — changing the glance mapping (primary/
  // delta/state fields) also needs to restart the effect so the new mapping is picked
  // up. Serialising avoids depending on the raw object identity which changes on
  // every board commit even when the logical value is unchanged (M5 fix).
  const glanceKey = useMemo(
    () => JSON.stringify(tile.glance),
    [tile.glance],
  );

  useEffect(() => {
    const adapter = registry.get(tile.type);
    if (!adapter) {
      setVm({
        dataState: 'error',
        glanceState: 'stale',
        slots: { primary: '', delta: null },
        label,
        fetchedAt: null,
      });
      setData(null);
      return;
    }

    // Capture adapter in a const so TS narrows it for use inside nested fns.
    const resolvedAdapter = adapter;

    // Registry-driven clientDirect routing (ADR-004).
    // One mechanism replaces the old hardcoded `tile.type === 'clock.local'` check,
    // so clock and calendar share a single code path.
    const typeDef = getTileTypeDef(tile.type);

    if (typeDef.noFetch) {
      // noFetch tiles: local data, no network. Three sub-families:
      //   clock.local      — 1-second interval, data from Date
      //   note.sticky      — no interval (content changes only on edit via configureTile)
      //   todo.list        — no interval (content changes only on edit via configureTile)
      //   countdown.event  — 60-second interval (keeps remaining time live)
      //
      // Each param change triggers a new effect run via sourceKey dependency,
      // which restarts any interval — fixing the stale-closure (D5a).
      const glanceMap: GlanceMap = tile.glance;

      if (tile.type === 'clock.local') {
        // Clock: 1-second interval, data computed from local Date.
        // Formatting rules (period-free primary, tz applied with unknown-zone
        // fallback) live in the pure formatClockData — see adapters/clock/format.ts.
        const tz = (tile.source?.params as { tz?: string } | undefined)?.tz
          ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
        const rawFormat = (tile.source?.params as { format?: string } | undefined)?.format;
        const format: '12h' | '24h' = rawFormat === '24h' ? '24h' : '12h';

        function updateClock(): void {
          const clockData = formatClockData(new Date(), { tz, format });
          // clampGlanceViewModel on EVERY direct-set path (review MEDIUM): the
          // central clamp only runs inside viewModelFromResponse, so noFetch
          // branches must apply it themselves — note previews are raw user
          // text up to 80 chars and would otherwise reach the primary slot
          // (and the tile's accessible name) unclamped. Idempotent no-op for
          // clock/todo/countdown output.
          setVm(clampGlanceViewModel({
            ...resolvedAdapter.glanceView(clockData, glanceMap),
            label,
          }));
          setNeedsConnect(false);
        }
        updateClock();
        const interval = setInterval(updateClock, 1000);
        return () => clearInterval(interval);
      }

      if (tile.type === 'note.sticky') {
        // Note: params ARE the content; re-derive when params change (sourceKey dep).
        // No interval needed — content changes only via configureTile.
        const params = tile.source?.params as { text?: string } | undefined;
        const noteParams = { text: params?.text ?? '' };
        const noteData = computeNoteData(noteParams);
        setVm(clampGlanceViewModel({ ...localNoteAdapter.glanceView(noteData, glanceMap), label }));
        setData(noteData as unknown as Record<string, unknown>);
        setNeedsConnect(false);
        // Return undefined — no cleanup needed (no interval).
        return;
      }

      if (tile.type === 'todo.list') {
        // Todo: params ARE the content; re-derive when params change (sourceKey dep).
        // No interval needed — content changes only via configureTile.
        const params = tile.source?.params as { items?: unknown[] } | undefined;
        const rawItems = Array.isArray(params?.items) ? params.items : [];
        // Type-safe: each item has text+done from the schema.
        const safeItems = rawItems.filter(
          (it): it is { text: string; done: boolean } =>
            typeof it === 'object' && it !== null &&
            typeof (it as { text?: unknown }).text === 'string' &&
            typeof (it as { done?: unknown }).done === 'boolean',
        );
        const todoData = computeTodoData({ items: safeItems });
        setVm(clampGlanceViewModel({ ...localTodoAdapter.glanceView(todoData, glanceMap), label }));
        setData(todoData as unknown as Record<string, unknown>);
        setNeedsConnect(false);
        return;
      }

      if (tile.type === 'countdown.event') {
        // Countdown: derived from params + current time; 60s interval to keep live.
        const params = tile.source?.params as { label?: string; targetISO?: string } | undefined;
        const countdownParams = {
          label: params?.label ?? '',
          targetISO: params?.targetISO ?? new Date(Date.now() + 7 * 24 * 3600 * 1000).toISOString(),
        };

        function updateCountdown(): void {
          const countdownData = computeCountdownData(countdownParams, new Date());
          setVm(clampGlanceViewModel({ ...localCountdownAdapter.glanceView(countdownData, glanceMap), label }));
          setData(countdownData as unknown as Record<string, unknown>);
          setNeedsConnect(false);
        }
        updateCountdown();
        const interval = setInterval(updateCountdown, 60_000);
        return () => clearInterval(interval);
      }

      // Fallback for any future noFetch types: show loading/neutral state.
      setVm({ dataState: 'ok', glanceState: 'neutral', slots: { primary: '', delta: null }, label, fetchedAt: Date.now() });
      setNeedsConnect(false);
      return;
    }

    if (typeDef.clientDirect) {
      // ---- rss.feed: tokenless direct fetch (ADR-005 user-source) ----
      // No OAuth token; browser fetches the user's feedUrl directly.
      // Failure states mapped to glance chrome per CORS-honesty UX (ADR-005):
      //   cors    → delta "Can't access"   (honest: "doesn't allow browser access")
      //   timeout → delta "Feed timed out"
      //   invalid → delta "Not a valid feed"
      //   error   → delta "Feed error"
      // needsConnect is always false for RSS (no account to connect).
      if (tile.type === 'rss.feed') {
        let cancelledRss = false;

        async function fetchRss(): Promise<void> {
          const { fetchFeed } = await import('../../adapters/rss/fetch-feed.js');
          const { rssAdapter: rssAdapterLocal } = await import('../../adapters/rss/adapter.js');
          const params = tile.source?.params as { feedUrl?: string; maxItems?: number } | undefined;
          const feedUrl = (params?.feedUrl as string | undefined) ?? '';
          const maxItems = (params?.maxItems as number | undefined) ?? 5;

          if (!feedUrl) {
            if (!cancelledRss) {
              setVm({ dataState: 'error', glanceState: 'stale', slots: { primary: '', delta: 'No feed URL' }, label, fetchedAt: null });
              setData(null);
              setNeedsConnect(false);
            }
            return;
          }

          const result = await fetchFeed(feedUrl, maxItems);
          if (cancelledRss) return;

          if (!result.ok) {
            // CORS-honesty UX: map failure type to short honest delta (≤20 chars each)
            const deltaMap: Record<string, string> = {
              cors: "Can't access",      // 12 chars — "this feed doesn't allow direct access"
              timeout: 'Feed timed out', // 14 chars
              invalid: 'Not a valid feed', // 16 chars
              error: 'Feed error',        // 10 chars
            };
            setVm({
              dataState: 'error',
              glanceState: 'stale',
              slots: { primary: '', delta: deltaMap[result.failure] ?? 'Feed error' },
              label,
              fetchedAt: null,
            });
            setData(null);
            setNeedsConnect(false);
            return;
          }

          const glanceVm = rssAdapterLocal.glanceView(result.data, tile.glance);
          setVm({ ...glanceVm, label });
          // Stash items under _items for RssDetail (mirrors calendar _events pattern)
          setData({ ...result.data, _items: result.data.items } as unknown as Record<string, unknown>);
          setNeedsConnect(false);
        }

        void fetchRss();
        const intervalSec = clientDirectIntervalSec(tile);
        const refreshInterval = setInterval(() => { void fetchRss(); }, intervalSec * 1000);

        return () => {
          cancelledRss = true;
          clearInterval(refreshInterval);
        };
      }

      // ---- calendar.peek: OAuth token + clientDirect (existing path) ----
      // clientDirect + !noFetch tiles (calendar.peek): fetch direct to upstream
      // using the user's own OAuth token via google-client. Never calls CacheClient.
      // Refresh is driven by tile.source.refresh.intervalSec (default 300s).
      let cancelledDirect = false;

      async function fetchCalendar(): Promise<void> {
        // Lazy import to keep the calendar client out of the clock/shared bundle path
        const {
          ensureToken,
          fetchCalendarData,
          getConnectState,
        } = await import('../../adapters/calendar/google-client.js');

        const connectState = getConnectState();

        // not_configured: env issue — show calm note, no connect affordance
        if (connectState === 'not_configured') {
          if (!cancelledDirect) {
            setVm({
              dataState: 'error',
              glanceState: 'stale',
              slots: { primary: 'No key', delta: 'Not configured' },
              label,
              fetchedAt: null,
            });
            setData(null);
            setNeedsConnect(false);
          }
          return;
        }

        // Try to get a token (silent only here — interactive is from user gesture)
        const token = await ensureToken('silent');

        if (!token) {
          if (!cancelledDirect) {
            // disconnected or expired after silent attempt: show Connect affordance
            setVm({
              dataState: 'ok',
              glanceState: 'neutral',
              slots: { primary: 'Connect', delta: 'Tap to connect' },
              label,
              fetchedAt: null,
            });
            setData(null);
            setNeedsConnect(true);
          }
          return;
        }

        const params = tile.source?.params as { calendarId?: string; maxEvents?: number; lookaheadHours?: number } | undefined;
        const calendarParams = {
          calendarId: (params?.calendarId ?? 'primary') as 'primary',
          maxEvents: params?.maxEvents ?? 3,
          lookaheadHours: params?.lookaheadHours ?? 12,
        };

        try {
          const result = await fetchCalendarData(calendarParams, token);
          if (cancelledDirect) return;

          const { calendarAdapter } = await import('../../adapters/calendar/adapter.js');
          const glanceVm = calendarAdapter.glanceView(result.data, tile.glance);
          setVm({ ...glanceVm, label });
          // Merge data + events so detail sheet can access both
          setData({ ...result.data, _events: result.events } as Record<string, unknown>);
          setNeedsConnect(false);
        } catch (err) {
          if (cancelledDirect) return;
          const msg = err instanceof Error ? err.message : '';
          if (msg === 'calendar:expired') {
            // Token expired mid-request; silent re-acquire was attempted inside
            // google-client. On next poll the state machine will handle it.
            setVm({
              dataState: 'ok',
              glanceState: 'neutral',
              slots: { primary: 'Connect', delta: 'Session expired' },
              label,
              fetchedAt: null,
            });
            setNeedsConnect(true);
          } else {
            setVm({
              dataState: 'error',
              glanceState: 'stale',
              slots: { primary: '', delta: null },
              label,
              fetchedAt: null,
            });
            setNeedsConnect(false);
          }
          setData(null);
        }
      }

      // Initial fetch
      void fetchCalendar();

      // Refresh on interval. `refresh` lives at the TILE top level (tile.ts
      // schema) — NOT under source; reading tile.source.refresh silently
      // always defaulted to 300s (review HIGH-1).
      const intervalSec = clientDirectIntervalSec(tile);
      const refreshInterval = setInterval(() => { void fetchCalendar(); }, intervalSec * 1000);

      return () => {
        cancelledDirect = true;
        clearInterval(refreshInterval);
      };
    }

    // Shared-data fetch tiles: call through the CacheClient (unchanged path).
    let cancelled = false;
    // Defensive reset (review LOW-1): a stale needsConnect from a prior
    // clientDirect life of this component must never render a Connect overlay
    // on a shared-data tile.
    setNeedsConnect(false);

    if (!tile.source) {
      setVm({
        dataState: 'error',
        glanceState: 'stale',
        slots: { primary: '', delta: null },
        label,
        fetchedAt: null,
      });
      setData(null);
      return;
    }

    const req: CacheRequest = {
      type: tile.type,
      provider: tile.source.provider,
      params: tile.source.params,
    };

    void cache.fetch(req).then((res) => {
      if (cancelled) return;
      setVm(viewModelFromResponse(res, adapter, tile.glance, label));
      // Expose the raw normalized data for enriched detail views (D6).
      // Only data-bearing states carry a usable payload; error/rateLimited do not.
      if (isCacheData(res)) {
        setData(res.data as Record<string, unknown>);
      } else {
        setData(null);
      }
    });

    return () => { cancelled = true; };
    // sourceKey restarts when tz/format params change (D5a fix).
    // glanceKey restarts when the glance mapping changes (M5 fix).
    // Raw tile.source and tile.glance objects are intentionally excluded — they
    // change identity on every board commit even when logically unchanged, which
    // would restart the clock interval on every edit-mode drag. The serialised
    // keys above are the correct stable dependencies.
  }, [tile.id, tile.type, sourceKey, glanceKey, label, cache, registry]); // eslint-disable-line react-hooks/exhaustive-deps

  return { vm, data, needsConnect };
}

/**
 * Resolve a clientDirect tile's polling interval in seconds.
 * `refresh` is a TOP-LEVEL tile field (see tile.ts) — regression-tested in
 * client-direct-interval.test.ts after review HIGH-1 found a wrong-path read
 * that silently ignored every configured cadence.
 */
export function clientDirectIntervalSec(tile: Pick<Tile, 'refresh'>): number {
  return tile.refresh?.intervalSec ?? 300;
}
